import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { permissionsApi, usersApi, type User } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const editSchema = z.object({
  displayName: z.string().max(255).optional(),
  status: z.enum(['active', 'suspended']),
  roleIds: z.array(z.string().uuid()),
});

type EditFormValues = z.infer<typeof editSchema>;

interface EditUserDialogProps {
  user: User | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditUserDialog({ user, open, onOpenChange }: EditUserDialogProps) {
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: catalog } = useQuery({
    queryKey: ['permissions-catalog'],
    queryFn: () => permissionsApi.getCatalog(),
    enabled: open,
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty },
  } = useForm<EditFormValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      displayName: '',
      status: 'active',
      roleIds: [],
    },
  });

  useEffect(() => {
    if (user && open) {
      reset({
        displayName: user.displayName || '',
        status: user.status === 'suspended' ? 'suspended' : 'active',
        roleIds: user.roles.map((r) => r.id),
      });
      setSubmitError(null);
    }
  }, [user, open, reset]);

  const mutation = useMutation({
    mutationFn: async (values: EditFormValues) => {
      if (!user) throw new Error('No user selected');

      const currentRoleIds = user.roles.map((r) => r.id).sort();
      const nextRoleIds = [...values.roleIds].sort();
      const rolesChanged =
        currentRoleIds.length !== nextRoleIds.length ||
        currentRoleIds.some((id, i) => id !== nextRoleIds[i]);

      const profileChanged =
        (user.displayName || '') !== (values.displayName || '') ||
        user.status !== values.status;

      if (profileChanged) {
        await usersApi.update(user.id, {
          displayName: values.displayName || undefined,
          status: values.status,
        });
      }
      if (rolesChanged) {
        await usersApi.updateRoles(user.id, values.roleIds);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onOpenChange(false);
    },
    onError: (err: Error) => {
      setSubmitError(err.message || 'Something went wrong.');
    },
  });

  const onSubmit = (values: EditFormValues) => {
    setSubmitError(null);
    mutation.mutate(values);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit user</DialogTitle>
          <DialogDescription>
            {user ? user.email : ''}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-display-name">Display name</Label>
            <Input id="edit-display-name" {...register('displayName')} />
            {errors.displayName && (
              <p className="text-sm text-destructive">{errors.displayName.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-status">Status</Label>
            <select
              id="edit-status"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              {...register('status')}
            >
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          <div className="space-y-2">
            <Label>Roles</Label>
            <Controller
              name="roleIds"
              control={control}
              render={({ field }) => (
                <div className="space-y-2 rounded-md border p-3 max-h-48 overflow-y-auto">
                  {!catalog ? (
                    <p className="text-sm text-muted-foreground">Loading roles…</p>
                  ) : catalog.roles.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No roles available.</p>
                  ) : (
                    catalog.roles.map((role) => {
                      const checked = field.value.includes(role.id);
                      return (
                        <label
                          key={role.id}
                          className="flex items-start gap-2 cursor-pointer text-sm"
                        >
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={checked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                field.onChange([...field.value, role.id]);
                              } else {
                                field.onChange(field.value.filter((id) => id !== role.id));
                              }
                            }}
                          />
                          <span>
                            <span className="font-medium">{role.name}</span>
                            {role.description && (
                              <span className="block text-xs text-muted-foreground">
                                {role.description}
                              </span>
                            )}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              )}
            />
          </div>

          {submitError && (
            <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-2">
              {submitError}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending || !isDirty}>
              {mutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
